import { CircleInfoIcon } from "@xola/icons";
import { flash, formatTime, Input, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, now, nullableNumber } from "common";
import dayjs from "dayjs";
import { isNil, noop } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { DatepickerPreference, DisplayType, Permission, PriceDisplay, Timeslot, UnitType } from "types";
import { object, string } from "yup";
import { api } from "../../../../api/api";
import { ScrollToFieldError } from "../../../../helpers/validationHelper";
import { useSeller } from "../../../../hooks/useAccount";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useModal } from "../../../../hooks/useVisibility";
import { NumberInlineValuePopover } from "../../../Common/NumberInlineValuePopover";
import { RadioInput } from "../../../Common/RadioInput";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { ArrivalDatePicker } from "../../../DatePicker/ArrivalDatePicker";
import { TimeslotInfo } from "../../../DatePicker/ArrivalTimePicker";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { DisplayPriceInlinePopover } from "./DisplayPriceInlinePopover";
import { InlineTimeslotPicker } from "./InlineTimeslotPicker";
import { NoTranslate } from "@/components/Common/NoTranslate";

// Dummy data for the datepicker
// Added isNextDay flag for times to differentiate if its nextDate in dummy data, this field doesn't exist in timeslots
const dummyData = {
    count: [0, 5, 12, 3, 15, 15, 0, 0, 7, 9, 19, 20, 20, 20, 18],
    price: [0, 109, 109, 99, 99, 99, 0, 0, 109, 109, 99, 99, 99, 89, 89],
    none: [0, 1, 1, 1, 1, 1, 0, 0, 1, 1, 1, 1, 1, 1, 1],
    slots: [0, 7, 2, 1, 0, 8, 2, 5, 1, 0, 2, 3, 0, 5, 8],
    times: [
        { time: 215 },
        { time: 800 },
        { time: 900 },
        { time: 1100 },
        { time: 1200 },
        { time: 2200 },
        { time: 100, isNextDay: true },
        { time: 200, isNextDay: true },
        { time: 230, isNextDay: true },
    ],
};

const lastScheduledDate = dayjs().set("date", 29).startOf("day");

const unavailableMessage = "No schedule has been set for the selected date. Please select another date.";
const defaultDatePickerPreference: DatepickerPreference = {
    displayCountMax: null,
    displayType: DisplayType.PRICE,
    priceDisplay: PriceDisplay.LOWEST,
    showUnavailableTimes: false,
    unavailableMessage: null,
    unavailableScheduleMessage: null,
    showNextDayTimeslotsTill: null,
    showCurrentDayTimeslotsAfter: null,
};

const validationSchema = object().shape({
    displayCountMax: nullableNumber().moreThan(0, "should be greater than 0"),
    unavailableMessage: string()
        .nullable()
        .test("unavailableMessage", "is required", (value) => {
            return value === null || (value !== undefined && value.length > 0);
        }),
    unavailableScheduleMessage: string()
        .nullable()
        .test("unavailableScheduleMessage", "is required", (value) => {
            return value === null || (value !== undefined && value.length > 0);
        }),
});

export const DatePickerPreference = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const showMaxCount = useModal(false);
    const isVirtualExperiencesEnabled = isGranted(Permission.VIRTUAL_MEETINGS_VIEW);
    const appliesToCheckoutText = `Applies only to checkout.${
        isVirtualExperiencesEnabled ? " Does not apply to virtual experiences." : ""
    }`;

    const initialValues = {
        ...defaultDatePickerPreference,
        ...(seller.preferences.datepickerPreference ? seller.preferences.datepickerPreference : {}),
    };

    const { mutateAsync: save } = useMutation(async (payload: DatepickerPreference) => {
        return api.post(`/users/${seller.id}/preferences/datepicker`, payload);
    });

    const handleSubmit = async (values: DatepickerPreference) => {
        await save(values);
        invalidate("sellers", seller.id);
        flash.success("Successfully updated datepicker preference");
    };

    return (
        <ContentLayout isWide>
            <h2 className="mt-4">Date Picker Preferences</h2>

            <XolaFormik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ handleSubmit, values, setFieldValue, isSubmitting, handleReset, errors }) => {
                    const handleShowNextdayTimeslotsTillChange = (value: number) => {
                        if (
                            !isNil(values.showNextDayTimeslotsTill) &&
                            !isNil(values.showCurrentDayTimeslotsAfter) &&
                            values.showNextDayTimeslotsTill === values.showCurrentDayTimeslotsAfter
                        ) {
                            setFieldValue("showNextDayTimeslotsTill", value);
                            setFieldValue("showCurrentDayTimeslotsAfter", value);
                        } else if (!isNil(values.showNextDayTimeslotsTill)) {
                            setFieldValue("showNextDayTimeslotsTill", value);
                        }
                    };

                    const handleShowCurrentDateTimeslotsAfterChange = (value: number) => {
                        if (!isNil(values.showCurrentDayTimeslotsAfter)) {
                            setFieldValue("showCurrentDayTimeslotsAfter", value);
                        }
                    };

                    const maxCount = values.displayCountMax ? String(values.displayCountMax) : "0";
                    const maxCountError = errors.displayCountMax ? "Count should be greater than 0" : undefined;
                    return (
                        <>
                            <ContentLayout.Section
                                title="Date Picker Display"
                                description="Customize the information that is displayed to your customers during checkout on your website."
                            >
                                <div className="flex flex-col space-y-5">
                                    <div className="flex flex-col space-y-2">
                                        <div className="mb-6 space-y-2">
                                            <RadioInput
                                                checked={values.displayType === DisplayType.PRICE}
                                                onChange={() => setFieldValue("displayType", DisplayType.PRICE)}
                                            >
                                                <span>Show the</span>
                                                <DisplayPriceInlinePopover
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                />
                                                <span>price per person</span>
                                            </RadioInput>

                                            <RadioInput
                                                checked={values.displayType === DisplayType.COUNT}
                                                onChange={() => setFieldValue("displayType", DisplayType.COUNT)}
                                            >
                                                <span>Show the open seat count</span>
                                            </RadioInput>

                                            {values.displayType === DisplayType.COUNT && (
                                                <div className="flex items-center space-x-2 pl-4">
                                                    <Switch
                                                        data-testid="date-picker-preference-conditional-open-seats"
                                                        isChecked={!isNil(values.displayCountMax)}
                                                        onChange={(value: boolean) =>
                                                            setFieldValue("displayCountMax", value ? 0 : null)
                                                        }
                                                    />

                                                    <span>
                                                        Conditionally show the open seat count when count is {"<="}
                                                    </span>

                                                    <NumberInlineValuePopover
                                                        text={maxCount}
                                                        error={maxCountError}
                                                        value={values.displayCountMax ?? 0}
                                                        isOpen={showMaxCount.isVisible}
                                                        onOpenChange={showMaxCount.set}
                                                        onChange={(value) => setFieldValue("displayCountMax", value)}
                                                    />
                                                </div>
                                            )}

                                            <RadioInput
                                                checked={values.displayType === DisplayType.NONE}
                                                onChange={() => setFieldValue("displayType", DisplayType.NONE)}
                                            >
                                                <span>None</span>
                                            </RadioInput>
                                        </div>

                                        <UnavailableSlotSettings
                                            values={values}
                                            errors={errors}
                                            onChange={(key: keyof DatepickerPreference, value) =>
                                                setFieldValue(key, value)
                                            }
                                        />

                                        <div className="flex">
                                            <Switch
                                                size="medium"
                                                isChecked={!isNil(values.showNextDayTimeslotsTill)}
                                                onChange={(value: boolean) =>
                                                    setFieldValue("showNextDayTimeslotsTill", value ? 300 : null)
                                                }
                                            />
                                            <span className="ml-2">
                                                Show time slots past midnight
                                                {isNil(values.showNextDayTimeslotsTill) ? null : " until"}
                                            </span>
                                            {isNil(values.showNextDayTimeslotsTill) ? null : (
                                                <InlineTimeslotPicker
                                                    value={values.showNextDayTimeslotsTill}
                                                    onChange={handleShowNextdayTimeslotsTillChange}
                                                />
                                            )}

                                            <Tooltip content={appliesToCheckoutText} placement="bottom">
                                                <CircleInfoIcon className="ml-1" />
                                            </Tooltip>
                                        </div>

                                        <div className="flex">
                                            <Switch
                                                size="medium"
                                                isChecked={!isNil(values.showCurrentDayTimeslotsAfter)}
                                                onChange={(value: boolean) =>
                                                    setFieldValue("showCurrentDayTimeslotsAfter", value ? 300 : null)
                                                }
                                            />

                                            <span className="ml-2">
                                                {isNil(values.showCurrentDayTimeslotsAfter)
                                                    ? "Hide early morning time slots"
                                                    : "Hide time slots earlier than"}
                                            </span>
                                            {isNil(values.showCurrentDayTimeslotsAfter) ? null : (
                                                <InlineTimeslotPicker
                                                    value={values.showCurrentDayTimeslotsAfter}
                                                    onChange={handleShowCurrentDateTimeslotsAfterChange}
                                                />
                                            )}

                                            <Tooltip content={appliesToCheckoutText} placement="bottom">
                                                <CircleInfoIcon className="ml-1" />
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <h3>Preview</h3>
                                        <DatePickerPreview values={values} />
                                    </div>
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton className="self-start" isLoading={isSubmitting} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>

                                <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

interface SettingsProps {
    values: DatepickerPreference;
    errors: any;
    onChange: (key: keyof DatepickerPreference, value: any) => void;
}

const UnavailableSlotSettings = ({ values, errors, onChange }: SettingsProps) => {
    return (
        <div className="flex flex-col space-y-2">
            <Switch.Group>
                <Switch
                    data-testid="date-picker-preference-show-unavailable"
                    isChecked={!!values.showUnavailableTimes}
                    onChange={(value: boolean) => onChange("showUnavailableTimes", value)}
                />
                <Switch.Label direction="right">Show unavailable date and time slots</Switch.Label>
            </Switch.Group>

            <div className="flex flex-col space-y-2">
                <Switch.Group>
                    <Switch
                        data-testid="date-picker-preference-show-message-no-availability"
                        isChecked={!isNil(values.unavailableMessage)}
                        onChange={(value: boolean) => onChange("unavailableMessage", value ? "" : null)}
                    />

                    <Switch.Label direction="right">Show a custom message for dates with 0 availability</Switch.Label>
                </Switch.Group>
                {isNil(values.unavailableMessage) ? null : (
                    <Input
                        maxLength={15}
                        value={values.unavailableMessage}
                        placeholder="Eg: Sold out"
                        isError={!!errors.unavailableMessage}
                        className="max-w-40"
                        onChange={(e: any) => onChange("unavailableMessage", e.target.value)}
                    />
                )}
            </div>

            <div className="flex flex-col space-y-2">
                <Switch.Group className="items-start">
                    <Switch
                        data-testid="date-picker-preference-show-message-after-last-active"
                        isChecked={!isNil(values.unavailableScheduleMessage)}
                        onChange={(value: boolean) =>
                            onChange("unavailableScheduleMessage", value ? unavailableMessage : null)
                        }
                    />

                    <Switch.Label direction="right">
                        Show a custom message for dates that fall after your last active schedule’s final date
                    </Switch.Label>
                </Switch.Group>

                {isNil(values.unavailableScheduleMessage) ? null : (
                    <Input
                        className="max-w-[560px]"
                        value={values.unavailableScheduleMessage}
                        isError={!!errors.unavailableScheduleMessage}
                        onChange={(e: any) => onChange("unavailableScheduleMessage", e.target.value)}
                    />
                )}
            </div>
        </div>
    );
};

const DatePickerPreview = ({ values }: { values: DatepickerPreference }) => {
    const bookableDates = getBookableDates();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const slots = bookableDates[formatDate(selectedDate)] ?? [];
    const filteredSlots = values.showUnavailableTimes ? slots : slots.filter((slot) => slot.canBook);
    const [selectedTime, setSelectedTime] = useState(slots?.[0]?.time);

    const handleDisabledDays = (date: Date) => {
        const dt = formatDate(date);
        const cannotBookDate = !bookableDates[dt];
        return cannotBookDate || hasNoSchedules(date);
    };

    return (
        <>
            <ArrivalDatePicker
                month={now()}
                disabledDays={handleDisabledDays}
                bookableDates={bookableDates}
                popover={false}
                datePickerPreference={values}
                lastScheduledDate={formatDate(lastScheduledDate)}
                onChange={setSelectedDate}
                onMonthChange={noop}
            />

            <div className="grid grid-cols-4 gap-2 pt-4">
                {filteredSlots.map((timeslot) => {
                    const isNextDay = timeslot.date !== formatDate(selectedDate, DateFormat.DATE_ISO);

                    let shouldShowTimeslot = true;

                    if (timeslot.time) {
                        if (isNextDay) {
                            if (
                                isNil(values.showNextDayTimeslotsTill) ||
                                timeslot.time > values.showNextDayTimeslotsTill
                            ) {
                                // We should not show timeslot if we have showNextDayTimeslotsTill value and time is more than limit
                                // for eg if we configure to show next day timeslots till 5am, then all next day timeslots after 5am should nto show in today's
                                shouldShowTimeslot = false;
                            }
                        } else if (
                            !isNil(values.showCurrentDayTimeslotsAfter) &&
                            timeslot.time < values.showCurrentDayTimeslotsAfter
                        ) {
                            // We should not show timeslots which are less than the value configured in showCurrentDayTimeslotsAfter
                            // for eg, if we configire to show current day timeslots after 10am, then timeslots before 10am should not show
                            shouldShowTimeslot = false;
                        }
                    }

                    return shouldShowTimeslot ? (
                        <Tooltip
                            placement="bottom"
                            content={<NextdayTooltip timeslot={timeslot} />}
                            disabled={!isNextDay}
                        >
                            <TimeslotButton
                                key={timeslot.time}
                                timeslot={timeslot}
                                isActive={timeslot.time === selectedTime}
                                isNextDay={isNextDay}
                                onClick={() => setSelectedTime(timeslot.time)}
                            />
                        </Tooltip>
                    ) : null;
                })}
            </div>
        </>
    );
};

const NextdayTooltip = ({ timeslot }: { timeslot: Timeslot }) => {
    const nextDate = formatDate(timeslot.date, DateFormat.DATE);
    return (
        <div className="text-center text-sm">
            <div>Next Day</div>
            <div>({nextDate})</div>
        </div>
    );
};

const hasNoSchedules = (date: Date) => {
    return now(date).isAfter(lastScheduledDate);
};

interface TimeslotButtonProps {
    timeslot: Timeslot;
    isActive: boolean;
    isNextDay: boolean;
    onClick: (time: number) => void;
}

const TimeslotButton = ({ timeslot, isActive, isNextDay, onClick }: TimeslotButtonProps) => {
    const handleClick = () => {
        if (timeslot.time) {
            onClick(timeslot.time);
        }
    };

    return (
        <SecondaryButton
            disabled={!timeslot.canBook}
            className={clsx(
                "h-11 w-full whitespace-nowrap py-4",
                !timeslot.canBook && "bg-gray-light",
                isActive && "border border-blue-light bg-blue-lighter",
            )}
            onClick={handleClick}
        >
            <div className="m-1 space-y-0.5">
                <NoTranslate as="div" className="font-normal">
                    {formatTime(timeslot.time)}
                    {isNextDay ? <sup>+1</sup> : null}
                </NoTranslate>
                <div>
                    <TimeslotInfo timeslot={timeslot} />
                </div>
            </div>
        </SecondaryButton>
    );
};

const getBookableDates = () => {
    const bookableDates: { [key: string]: Timeslot[] } = {};
    const dates = [...Array.from({ length: 15 })].map((_, index) => formatDate(dayjs().date(14).add(index, "day")));
    for (const [index, date] of dates.entries()) {
        const currentDate = date;
        bookableDates[date] = [...Array.from({ length: dummyData.slots[index] })].map((_, _index) => {
            const { isNextDay } = dummyData.times[_index];
            const date = isNextDay ? formatDate(dayjs(currentDate).add(1, "day"), DateFormat.DATE_ISO) : currentDate;

            return {
                id: "dummyId",
                time: dummyData.times[_index].time,
                capacity: { open: dummyData.count[index], reserved: 0, max: 0 },
                date,
                purchasedUnits: { demographics: [], merchandise: [] },
                canBook: !!dummyData.none[index],
                event: { id: "dummyEventId" },
                product: "dummyProductId",
                arrivalDatetime: dayjs(),
                end: dayjs(),
                guideCount: 0,
                resourceUsageCount: 0,
                aggregatedBalance: 0,
                noteCount: 0,
                voucherCount: 0,
                waiverCount: 0,
                waiverParticipantCount: 0,
                otaPurchaseCount: 0,
                guestStatusCounts: [],
                canWaitlist: false,
                customerName: "dummy name",
                units: [
                    {
                        price: dummyData.count[index],
                        type: UnitType.DEMOGRAPHIC,
                        template: { id: "ID" },
                        id: "ID",
                        code: "",
                    },
                    {
                        price: dummyData.count[index + 1],
                        type: UnitType.DEMOGRAPHIC,
                        template: { id: "ID" },
                        id: "ID",
                        code: "",
                    },
                ],
                statusCounts: [],
            };
        });
    }

    return bookableDates;
};
